import { ReactNode } from "react";

import { ERoutes } from "@utils/constants";

import { Link } from "@/ui-kit";

type TFAQ = {
  title: string;
  description: ReactNode;
  /** TEMP review aid: shows an amber dot next to the question so the team can
      see which questions still need a content review. Remove once reviewed. */
  needsReview?: boolean;
};

export const MUSIC_CATALOG = (handleOpenQuestionFlow: () => void, handleOpenLoginDialog: () => void): TFAQ[] => [
  {
    title: "Is your work in the public domain & royalty-free?",
    description: (
      <span>
        While we allow the use of some of our music for free as background music on YouTube in line
        with our{" "}
        <Link size="inherit" href={ERoutes.FreeUseTerms}>
          free use terms
        </Link>{" "}
        and proper credit, our music is not public domain or royalty-free. All compositions and recordings are fully
        copyrighted and owned by Audio Sanctum®. Under no circumstances are you permitted to claim ownership or
        copyrights of our music.
      </span>
    ),
  },
  {
    title: "Can I use Audio Sanctum’s music for monetized videos?",
    description: (
      <span>
        Yes, you can use our music in monetized YouTube videos. Track copyright claims can only be whitelisted after
        they were connected to your video in the{" "}
        <Link size="inherit" href={ERoutes.AccountVideos}>
          claims page
        </Link>{" "}
        in your account.
      </span>
    ),
  },
  {
    title: "I Received A Copyright Claim or Strike, What Should I Do?",
    description: (
      <span>
        It depends what the reason is for the copyright claim:
        <br />
        <ol>
          <li>
            <span style={{ fontWeight: "bold" }}>You have a subscription</span>
            <br />
            <span>
              If you’ve subscribed and connected the channel, claims on it are auto-resolved (whitelisted) using your
              monthly claims, usually within a short time. You can see and manage them on your{" "}
              <Link size="inherit" href={ERoutes.AccountVideos}>
                Claims
              </Link>{" "}
              page. If a claim isn’t clearing, check that the channel is connected and that you still have claims left
              this month.
            </span>
          </li>
          <br />
          <li>
            <span style={{ fontWeight: "bold" }}>You’re using free use (with credit)</span>
            <br />
            <span>
              When you use our music in your videos, an automated copyright claim will be generated. Once your videos
              appear in our systems, we will verify if the free use terms apply and if the necessary copyright text has
              been included. If these requirements are met, we will remove the copyright claim. Please refrain from
              using YouTube's appeal process within the first seven days, as this could delay our review. If the claim
              persists after this period and you believe your content qualifies for free use, please{" "}
              <Link size="inherit" href={ERoutes.Contact}>
                contact us
              </Link>
            </span>
          </li>
          <br />
          <li>
            <span style={{ fontWeight: "bold" }}>A 3rd party copyright claim</span>
            <br />
            <span>
              If you receive a third-party copyright claim because YouTube's Content ID system has mistakenly identified
              your audio as another track, please note that Audio Sanctum's name will not be associated with these
              claims. Follow the appeal process on the platform where your content was flagged. Provide the third party
              with a detailed description of your music source (Audio Sanctum), its uniqueness compared to the claimed
              content, and your usage rights. Usually those are system errors that can be resolved easily. If your
              appeal on YouTube is unsuccessful and the third party refuses to remove their claim, please{" "}
              <Link size="inherit" href={ERoutes.Contact}>
                contact us
              </Link>{" "}
              for further assistance, although resolution cannot be guaranteed. Please note: in such a case, we can’t
              help you before you didn’t appeal one time and received the third party’s response.
            </span>
          </li>
          <br />
          <li>
            <span style={{ fontWeight: "bold" }}>We striked your content</span>
            <br />
            <span>
              If your content falls under our music creator terms, we usually try to contact you. If this was
              unsuccessful, we may issue a strike against your channel. In such
              cases, you will need to join our{" "}
              <Link size="inherit" href={ERoutes.MUSIC_CREATOR_PROGRAM}>
                music creator program
              </Link>{" "}
              to prevent future strikes. Compliance with our terms is crucial to ensure fair usage for all users and to
              honor our supportive approach to content creators.
            </span>
          </li>
          <br />
          <li>
            <span style={{ fontWeight: "bold" }}>Malicious registering of our Music</span>
            <br />
            <span>
              Unfortunately, some people attempt to abuse Content ID systems by placing wrongful, malicious copyright
              claims. While we cannot completely prevent this due to the architecture of YouTube's Content ID system, we
              respond quickly to such issues behind the scenes and are usually able to solve this in a very short time.
              If you face such a claim, please allow some time for it to be automatically removed. There should be no
              lasting negative effects on your channel.
            </span>
          </li>
        </ol>
      </span>
    ),
  },
  {
    title: "Do I always have to credit Audio Sanctum?",
    description: (
      <span>
        Yes. Every use carries a credit text. Under free use you include the full long credit text provided with the
        track. With a{" "}
        <Link size="inherit" href={ERoutes.Pricing}>
          subscription
        </Link>{" "}
        or a{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          Single YouTube video license
        </Link>{" "}
        you only add the short one-line credit in your video description. Credit texts have to stay visible for as long
        as the video uses the track.
      </span>
    ),
  },
  {
    title: "Can I translate the credit text?",
    description: (
      <span>
        No, descriptions that include translated credit text are not eligible for copyright claim whitelisting.
      </span>
    ),
  },
  {
    title: "Do you use AI to create your music?",
    description: (
      <span>
        No, all our music is crafted by real musicians and composers. We believe in using AI as a tool to aid
        creativity, not to remove the human soul from art.
      </span>
    ),
  },
  {
    title: "Can I modify the music for my projects?",
    description: (
      <span>
        You are free to adapt the music needed for standard video editing (shortening, fading, loudness changes). Any
        editing that changes the sound of our music (re-mixing, new audio productions, singing/rapping. pitch changing,
        stretching) as well as sampling needs a license and is not covered by free use. Please{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          get in touch
        </Link>{" "}
        to send your license request if you need this.
      </span>
    ),
  },
  {
    title: "Am I allowed to sample your music?",
    description: (
      <span>
        Sampling is only allowed if a sampling license was acquired beforehand (never under free use). Please use our{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          licensing formular
        </Link>{" "}
        to send your request. If these terms are breached, we will strike your content and take legal action.
      </span>
    ),
  },
  {
    title: "Am I allowed to sing or rap on top of your music?",
    description: (
      <span>
        Singing or rapping over our music is allowed only with a prior license and is not covered under free use terms.
        Please use our{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          licensing formular
        </Link>{" "}
        to send your request. If these terms are breached, we will strike your content.
      </span>
    ),
  },
  {
    title: "Do you offer WAV files for download?",
    description: (
      <span>
        Yes. High-quality WAV files are included with a{" "}
        <Link size="inherit" href={ERoutes.Pricing}>
          subscription
        </Link>{" "}
        (the full library is available as MP3 + WAV), and are also offered as a WAV option when you buy a single track
        license. For anything beyond that, feel free to{" "}
        <Link size="inherit" href={ERoutes.Contact}>
          contact us
        </Link>
        .
      </span>
    ),
  },
  {
    title: "Do you also offer exclusive custom music creation?",
    description: (
      <span>
        All of our music is created by real composers and artists, who spend a long time creating their music pieces.
        Therefore, custom music creation is available only for large-scale and well-funded projects due to the intensive
        nature of music production. Please{" "}
        <Link size="inherit" href={ERoutes.Contact}>
          send a request
        </Link>{" "}
        in case you want to talk about custom music creation.
      </span>
    ),
  },
  {
    title: "Do you offer extended track versions?",
    description: (
      <span>
        We plan to offer extended versions in the future but currently do not offer them as of now. We will notify you
        once they become available.
      </span>
    ),
  },
  {
    // HIDDEN from the /music FAQ (no tab mapping → excluded everywhere). Kept in
    // the array so the index-based references on the other FAQ pages + the tab
    // map don't shift. Safe to delete fully in a future FAQ refactor that keys
    // questions by id instead of array position.
    title: "I used up my download limit, what do I do?",
    description: (
      <span>
        If you've reached your download limit, please{" "}
        <Link size="inherit" href={ERoutes.Contact}>
          contact us
        </Link>{" "}
        with a valid reason to reset it.
      </span>
    ),
  },
  {
    title: "Am I eligible to use Audio Sanctum’s music for free?",
    description: (
      <span>
        In order to find out, please complete the{" "}
        <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
          Free, Subscription or License questionnaire
        </Link>
        , which gives you a result in under one minute.
      </span>
    ),
  },
  {
    title: "What do I need to do to use your music for free in my videos?",
    description: (
      <span>
        If you{" "}
        <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
          qualify for free use
        </Link>
        , you must first{" "}
        <Link size="inherit" component="button" onClick={handleOpenLoginDialog}>
          create an account
        </Link>
        . Once registered, browse and download the music you wish to use. After downloading, a pop-up will provide you
        with credit text, which you need to paste into your content's description. Then upload your video to YouTube and
        go to your{" "}
        <Link size="inherit" href={ERoutes.AccountVideos}>
          claims page
        </Link>
        . There you need to submit the video URL. We will then check the video and you'll be notified if your action is
        needed.
      </span>
    ),
  },
  {
    title: "Is there a limit on how many tracks I can use for free?",
    description: (
      <span>
        There is no limit to how many times you can use our free tracks, provided that you are{" "}
        <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
          eligible for free use
        </Link>
        .
      </span>
    ),
  },
  {
    title: "How can I further support you and give back?",
    description: (
      <span>
        Your support of Audio Sanctum and its musicians is highly appreciated! You can give back by purchasing items
        from our <Link href={ERoutes.Shop}>shop</Link>. Thank you so much!
      </span>
    ),
  },
  {
    title: "What types of licenses does Audio Sanctum offer?",
    description: (
      <span>
        Three routes, depending on what you need:
        <ol>
          <li>
            <span style={{ fontWeight: "bold" }}>Single YouTube Video License</span> — a one-time, fixed-price license
            you buy instantly for a track, to use on any one YouTube channel with just the short one-line credit.
          </li>
          <li>
            <span style={{ fontWeight: "bold" }}>Subscription</span> — ongoing coverage of copyright claims across the
            YouTube channels you connect (the short one-line credit text, monetization allowed). See{" "}
            <Link size="inherit" href={ERoutes.Pricing}>
              pricing
            </Link>
            .
          </li>
          <li>
            <span style={{ fontWeight: "bold" }}>Custom license</span> — for anything else (ads, film, TV, sampling,
            larger projects), always non-exclusive unless commissioned exclusively for your project. Submit your{" "}
            <Link size="inherit" href={ERoutes.Licensing}>
              license request
            </Link>
            .
          </li>
        </ol>
        For free background use on free video sites and social media, see our{" "}
        <Link size="inherit" href={ERoutes.FreeUseTerms}>
          free use terms
        </Link>
        .
      </span>
    ),
  },
  {
    title: "What is the process for licensing music from you?",
    description: (
      <span>
        For a single track on one channel, you can buy a <span style={{ fontWeight: "bold" }}>Single YouTube Video
        License</span> instantly from the{" "}
        <Link size="inherit" href={ERoutes.Catalog}>
          catalog page
        </Link>{" "}
        — pick the track, pay, upload your video and add the video URL to your{" "}
        <Link size="inherit" href={ERoutes.AccountVideos}>
          claims page
        </Link>
        , where it can be whitelisted.
        <br />
        <br />
        For a <span style={{ fontWeight: "bold" }}>custom license</span> (ads, film, TV, sampling, larger projects
        etc.): send us a description of your project via the{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          licensing form
        </Link>
        ; we’ll usually ask a few clarifying questions, then send an offer. Once we agree, you and Audio Sanctum sign the
        contract, you pay, and we send the high-quality files. We love to make things work with you!
      </span>
    ),
  },
  {
    title: "What does a license cost?",
    description: (
      <span>
        A <span style={{ fontWeight: "bold" }}>Single YouTube Video License</span> has a fixed price shown in-app when
        you buy it. A <span style={{ fontWeight: "bold" }}>subscription</span> is a flat monthly or annual price (see{" "}
        <Link size="inherit" href={ERoutes.Pricing}>
          pricing
        </Link>
        ). A <span style={{ fontWeight: "bold" }}>custom license</span> is quoted per project — every situation differs,
        so{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          tell us what you need
        </Link>{" "}
        and we’ll send an offer.
      </span>
    ),
  },
  {
    title: "What is the Audio Sanctum subscription?",
    description: (
      <span>
        A subscription covers the copyright claims our music generates on the YouTube channels you connect. You pick how
        many channels and monthly claims you need, connect your channel(s), and from then on, when you use our music and
        YouTube raises a copyright claim, it’s automatically resolved (licensed) — the short one-line credit text in the
        description, monetization allowed. See{" "}
        <Link size="inherit" href={ERoutes.Pricing}>
          pricing
        </Link>{" "}
        to get started.
      </span>
    ),
  },
  {
    title: "What’s included in a subscription?",
    description: (
      <span>
        Full Premium access as MP3 + WAV, coverage for the number of YouTube channels and monthly claims you choose,{" "}
        <span style={{ fontWeight: "bold" }}>auto-resolution of claims</span>, just a one-line credit,
        monetization allowed, and you can cancel anytime. You manage everything from your{" "}
        <Link size="inherit" href={ERoutes.AccountVideos}>
          Claims
        </Link>{" "}
        page.
      </span>
    ),
  },
  {
    title: "What is a copyright claim, and how does claim resolution work?",
    description: (
      <span>
        When you use our music in a video, YouTube’s Content ID automatically raises a copyright claim. It doesn’t harm
        your channel, but you can’t monetize the video anymore. In your Audio Sanctum{" "}
        <Link size="inherit" href={ERoutes.AccountVideos}>
          claims page
        </Link>
        , you connect your videos (free plan) or channels (subscription) and we detect those claims of our music in your
        content. You then can see every claim on your Claims page and get instructions how to resolve it.{" "}
        <span style={{ fontWeight: "bold" }}>Important:</span> a copyright claim is not a channel strike, it only detects
        copyright protected material or the rights holder in your videos.
      </span>
    ),
  },
  {
    title: "How many claims can I resolve?",
    description: (
      <span>
        In a free plan, you can resolve our free tracks an unlimited amount of times in the{" "}
        <Link size="inherit" href={ERoutes.AccountVideos}>
          claims page
        </Link>
        , if your video content is eligible and you added our credit text in your video description.
        <br />
        <br />
        On a subscription you can use our whole music library. You can choose how many channels and monthly claims you
        want to be covered each month and resolve those on the claims page. We always show you your remaining claims on
        there too.
      </span>
    ),
  },
  {
    title: "Do I still need credit text if I subscribe?",
    description: (
      <span>
        Yes, but only the short one-line credit text in your video description. The full long credit text is only
        required for free use.
      </span>
    ),
  },
  {
    title: "Can I use the subscription on livestreams or a music / ambient channel?",
    description: (
      <span>
        The subscription covers normal content where our tracks are used as background music only. Due to how YouTube
        handles copyright in livestreams, our music{" "}
        <span style={{ fontWeight: "bold" }}>cannot be used in livestreams at all</span>. It also does{" "}
        <span style={{ fontWeight: "bold" }}>not</span> cover content that uses our music as the main content (music or
        ambient channels) — for that, you’d need to become part of our{" "}
        <Link size="inherit" href={ERoutes.MUSIC_CREATOR_PROGRAM}>
          music creator program
        </Link>
        .
      </span>
    ),
  },
  {
    title: "Monthly or annual payments — what’s the difference?",
    description: (
      <span>
        No difference, same features — but you save 2 months (15%) if you pay the whole year upfront. You can switch
        between billing cycles from your{" "}
        <Link size="inherit" href={ERoutes.AccountSubscription}>
          subscription
        </Link>{" "}
        page.
      </span>
    ),
  },
  {
    title: "Can I cancel anytime?",
    description: (
      <span>
        Yes. You can cancel your subscription whenever you like from your{" "}
        <Link size="inherit" href={ERoutes.AccountSubscription}>
          subscription
        </Link>{" "}
        page, and you keep access until the end of the period you’ve already paid for.
      </span>
    ),
  },
  {
    title: "Subscription, single license or free use?",
    description: (
      <span>
        <span style={{ fontWeight: "bold" }}>Free use:</span> free tracks only, need credit text in your video
        descriptions, for eligible free YouTube use (check the{" "}
        <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
          free use test
        </Link>{" "}
        first). Monetization allowed.
        <br />
        <br />
        <span style={{ fontWeight: "bold" }}>Subscription:</span> Access to full music library, ongoing claim coverage
        across your channels, the short one-line credit text, monetization allowed.
        <br />
        <br />
        <span style={{ fontWeight: "bold" }}>Single YouTube Video License:</span> a one-off, fixed-price license for a
        single track on one channel, with the short one-line credit text.
        <br />
        <br />
        <span style={{ fontWeight: "bold" }}>Custom license:</span> anything outside YouTube use (ads, film, TV,
        sampling etc.), via the{" "}
        <Link size="inherit" href={ERoutes.Licensing}>
          licensing form
        </Link>
        .
      </span>
    ),
  },
  {
    title: "Is it safe to use Audio Sanctum?",
    description: (
      <span>
        Yes — you stay fully in control. We never ask for your YouTube login and can&rsquo;t upload, edit, or post
        anything on your channel; payments are handled by Stripe (a globally trusted payment provider), so your card
        details go straight to them and are never stored on our servers; and we only keep the account information needed
        to run your subscription and clear your claims. No channel access, no stored card data, no hidden catches.
      </span>
    ),
  },
  {
    title: "Do you need access to my YouTube channel?",
    description: (
      <span>
        No. Connecting a channel simply tells us which channel to clear copyright claims on — it gives us no access to
        it. We can&rsquo;t log in, upload, change settings, read your messages, or post on your behalf. Copyright claims
        are resolved through YouTube&rsquo;s Content ID system on our side, never by signing in to your account.
      </span>
    ),
  },
  {
    title: "Do you get a share of my YouTube revenue?",
    description: (
      <span>
        No, your earnings are entirely yours. We never take a cut of your ad revenue or monetization. With the right
        coverage in place, you keep your videos monetized and keep 100% of what they make.
      </span>
    ),
  },
  {
    title: "Is my payment information secure?",
    description: (
      <span>
        Yes. All payments run through Stripe, one of the world&rsquo;s most trusted payment providers. Your card details
        are entered directly with Stripe — Audio Sanctum&rsquo;s servers never see or store your full card number,
        security code, or expiry.
      </span>
    ),
  },
  {
    title: "What do you do with my personal data?",
    description: (
      <span>
        We collect personal data needed to run your account, keep it secure, and improve our service under EU&rsquo;s
        GDPR compliance. For example: your email, billing and subscription details, your claims and licenses, basic
        usage data like searches and page views, and security logs such as sign-in records to protect your account. We
        never sell your data. You can download a full copy of your data or delete your account at any time. See our{" "}
        <Link size="inherit" href={ERoutes.Privacy}>
          Privacy Policy
        </Link>{" "}
        for the full picture.
      </span>
    ),
  },
  {
    title: "Will using your music get my channel a copyright strike?",
    description: (
      <span>
        No — not when you use our music correctly. A Content ID copyright claim is completely normal and is{" "}
        <span style={{ fontWeight: "bold" }}>not</span> a strike: it doesn&rsquo;t take your video down or harm your
        channel&rsquo;s standing. It only detects content of a rights holder in your video as well as highlights it and
        disables monetization. With a subscription, claims in regards to our whole music library on your connected
        channels can be resolved once you&rsquo;ve added the short one-line credit. Free accounts can resolve claims in
        regards to our free tracks, once you&rsquo;ve added the full long credit text.
        <br />
        <br />
        Strikes only come into play if our music-creator terms are broken or the content goes against our content rules
        — e.g. re-uploading our music as the main content of a music / ambient channel without joining the{" "}
        <Link size="inherit" href={ERoutes.MUSIC_CREATOR_PROGRAM}>
          music creator program
        </Link>{" "}
        or content that goes against our guidelines (violent content, abusive content etc). For the full breakdown,
        see &ldquo;I received a copyright claim or strike&rdquo; above.
      </span>
    ),
  },
];

export const FREE_USE_TERMS = (handleOpenQuestionFlow: () => void, handleOpenLoginDialog: () => void): TFAQ[] => {
  const MUSIC_CATALOG_FAQS = MUSIC_CATALOG(handleOpenQuestionFlow, handleOpenLoginDialog);
  return [
    MUSIC_CATALOG_FAQS[13],
    MUSIC_CATALOG_FAQS[1],
    MUSIC_CATALOG_FAQS[3],
    MUSIC_CATALOG_FAQS[6],
    MUSIC_CATALOG_FAQS[7],
    MUSIC_CATALOG_FAQS[8],
    MUSIC_CATALOG_FAQS[4],
    {
      title: "Do I need to register to access the free background music?",
      description: (
        <span>
          Yes, it is required to{" "}
          <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
            sign up
          </Link>
          . You then can download our free tracks and connect your uploaded videos in the{" "}
          <Link size="inherit" href={ERoutes.AccountVideos}>
            claims page
          </Link>{" "}
          in order to resolve the copyright claims. You also will receive email notifications every time your action is
          required on a claim.
        </span>
      ),
    },
    MUSIC_CATALOG_FAQS[14],
    MUSIC_CATALOG_FAQS[2],
    {
      title: "I need a custom license, where can I get one?",
      description: (
        <span>
          Please use our{" "}
          <Link size="inherit" href={ERoutes.Licensing}>
            licensing contact formular
          </Link>{" "}
          to send your license request.
        </span>
      ),
    },
    MUSIC_CATALOG_FAQS[15],
    {
      title: "How can I download the free background music tracks?",
      description: (
        <span>
          Please{" "}
          <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
            sign up
          </Link>{" "}
          to our music catalog. Once registered, you can download the music by clicking the download button next to each
          track and album. Ensure that you are{" "}
          <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
            eligible for free use
          </Link>
          . After downloading, you'll receive the necessary copyright text in a pop-up, which you must paste into the
          description of your YouTube video. Then, you need to connect your video in the{" "}
          <Link size="inherit" href={ERoutes.AccountVideos}>
            Claims
          </Link>{" "}
          page in order to remove the copyright claim.
        </span>
      ),
    },
    {
      title: "How can I support you further for what you offer?",
      description: MUSIC_CATALOG_FAQS[16].description,
    },
  ];
};

export const FREE_BACKGROUND_MUSIC = (
  handleOpenQuestionFlow: () => void,
  handleOpenLoginDialog: () => void,
): TFAQ[] => {
  const MUSIC_CATALOG_FAQS = MUSIC_CATALOG(handleOpenQuestionFlow, handleOpenLoginDialog);
  const FREE_USE_TERMS_FAQS = FREE_USE_TERMS(handleOpenQuestionFlow, handleOpenLoginDialog);
  return [
    MUSIC_CATALOG_FAQS[13],
    MUSIC_CATALOG_FAQS[1],
    MUSIC_CATALOG_FAQS[3],
    MUSIC_CATALOG_FAQS[6],
    MUSIC_CATALOG_FAQS[7],
    MUSIC_CATALOG_FAQS[8],
    MUSIC_CATALOG_FAQS[4],
    FREE_USE_TERMS_FAQS[7],
    MUSIC_CATALOG_FAQS[14],
    MUSIC_CATALOG_FAQS[2],
    FREE_USE_TERMS_FAQS[10],
    MUSIC_CATALOG_FAQS[15],
    FREE_USE_TERMS_FAQS[12],
    FREE_USE_TERMS_FAQS[13],
  ];
};

export const LICENSING = (handleOpenQuestionFlow: () => void, handleOpenLoginDialog: () => void): TFAQ[] => {
  const MUSIC_CATALOG_FAQS = MUSIC_CATALOG(handleOpenQuestionFlow, handleOpenLoginDialog);
  return [
    {
      title: "Can I also use your music for free?",
      description: (
        <span>
          We offer some free tracks in our library for free use on YouTube, if the use case meets our free use terms.
          Take this{" "}
          <Link size="inherit" component="button" onClick={handleOpenQuestionFlow}>
            quick questionnaire
          </Link>{" "}
          to find out if your use case is eligible for free use in under one minute.
        </span>
      ),
    },
    {
      title: "How do I obtain a license for a track from Audio Sanctum?",
      description: (
        <span>
          Please use the{" "}
          <Link size="inherit" href={ERoutes.Contact}>
            licensing contact formular
          </Link>{" "}
          to start the licensing process with us, we love to make things work with you!
        </span>
      ),
    },
    {
      title: "Once I obtain a license, can I use your music everywhere?",
      description: (
        <span>
          In our license agreement we will clearly define where and to which extent you are allowed to use Audio
          Sanctum’s music. Please use the{" "}
          <Link size="inherit" href={ERoutes.Contact}>
            licensing contact formular
          </Link>{" "}
          to start the licensing process with us, we love to make things work with you!
        </span>
      ),
    },
    {
      title: "Once I obtain a license, can I use your music forever?",
      description: (
        <span>
          In our license agreement we will clearly define for how long and to which extent you are allowed to use our
          music. Please use the{" "}
          <Link size="inherit" href={ERoutes.Contact}>
            licensing contact formular
          </Link>{" "}
          to start the licensing process with us, we love to make things work with you!
        </span>
      ),
    },
    MUSIC_CATALOG_FAQS[17],
    MUSIC_CATALOG_FAQS[19],
    MUSIC_CATALOG_FAQS[18],
  ];
};

export const LICENSING_FORM = (handleOpenQuestionFlow: () => void, handleOpenLoginDialog: () => void): TFAQ[] => {
  return LICENSING(handleOpenQuestionFlow, handleOpenLoginDialog);
};

// Subscription-focused FAQ for the Pricing page (the subscription questions +
// the license types / cost overview).
export const SUBSCRIPTION = (handleOpenQuestionFlow: () => void, handleOpenLoginDialog: () => void): TFAQ[] => {
  const C = MUSIC_CATALOG(handleOpenQuestionFlow, handleOpenLoginDialog);
  return [C[20], C[21], C[22], C[23], C[24], C[25], C[26], C[27], C[28], C[17], C[19]];
};
